/**
 * RDV Connector Admin View (couche 1 — readiness & import).
 *
 * Scope volontairement minimal :
 *   - Configuration (provider + host + mapping JSON)
 *   - Bouton Tester → rapport structuré 5 étages
 *   - Bouton Dry-run → preview mapping sur un échantillon
 *   - Sélecteur de mode (DISABLED / DRY_RUN / ACTIVE)
 *   - Tableau des 10 derniers sync runs + dernier état
 *
 * Pas de cockpit. Pas d'audit log UI (exposé via API).
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import {
  Plug, Play, FlaskConical, ShieldAlert, CheckCircle2, XCircle,
  AlertTriangle, Loader2, RefreshCw, Lock
} from "lucide-react";

const MODE_STYLES = {
  DISABLED: "bg-slate-100 text-slate-600",
  DRY_RUN: "bg-amber-100 text-amber-700",
  ACTIVE: "bg-emerald-100 text-emerald-700",
};

const READINESS_STYLES = {
  READY: "bg-emerald-50 text-emerald-700 border-emerald-200",
  PARTIAL: "bg-amber-50 text-amber-700 border-amber-200",
  NOT_READY: "bg-red-50 text-red-700 border-red-200",
  BLOCKED: "bg-purple-50 text-purple-700 border-purple-200",
};

const STAGE_ICON = {
  PASS: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  PARTIAL: <AlertTriangle className="w-4 h-4 text-amber-600" />,
  FAIL: <XCircle className="w-4 h-4 text-red-600" />,
  SKIP: <div className="w-4 h-4 rounded-full bg-slate-200" />,
  NOT_IMPLEMENTED: <ShieldAlert className="w-4 h-4 text-slate-500" />,
};

const DEFAULT_MAPPING = {
  patient_identifier_field: "",
  patient_identifier_type: "CIN",
  external_id_field: "",
  date_field: "",
  date_format: "ISO8601",
  timezone: "Africa/Casablanca",
  type_field: "",
  type_mapping: {},
  status_field: null,
  status_mapping: {},
  doctor_field: null,
  location_field: null,
  notes_field: null,
};

export default function RdvConnectorAdminView() {
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    providerType: "CUSTOM",
    host: "",
    port: "",
    database: "",
    username: "",
    password: "",
    tableName: "",
    syncFrequencyMinutes: 15,
    allowPrivateNetwork: false,
    privateNetworkJustification: "",
    fieldMapping: DEFAULT_MAPPING,
  });
  const [mappingJson, setMappingJson] = useState(JSON.stringify(DEFAULT_MAPPING, null, 2));
  const [reauthPwd, setReauthPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [dryRunning, setDryRunning] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [dryResult, setDryResult] = useState(null);
  const [sampleJson, setSampleJson] = useState('{\n  "example_field": "value"\n}');
  const [runs, setRuns] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes, rRes] = await Promise.all([
        api.get("/appointments/connector/providers"),
        api.get("/appointments/connector"),
        api.get("/appointments/connector/sync-runs?limit=10"),
      ]);
      setProviders(pRes.data.providers || []);
      if (cRes.data?.configured) {
        setConfig(cRes.data);
        setForm((f) => ({
          ...f,
          providerType: cRes.data.providerType || "CUSTOM",
          host: cRes.data.host || "",
          port: cRes.data.port || "",
          database: cRes.data.database || "",
          username: cRes.data.username || "",
          password: "",
          tableName: cRes.data.tableName || "",
          syncFrequencyMinutes: cRes.data.syncFrequencyMinutes || 15,
          allowPrivateNetwork: !!cRes.data.allowPrivateNetwork,
          privateNetworkJustification: cRes.data.privateNetworkJustification || "",
          fieldMapping: cRes.data.fieldMapping || DEFAULT_MAPPING,
        }));
        setMappingJson(JSON.stringify(cRes.data.fieldMapping || DEFAULT_MAPPING, null, 2));
      } else {
        setConfig(null);
      }
      setRuns(rRes.data.runs || []);
    } catch (e) {
      toast.error("Chargement du connecteur impossible");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveConfig = async () => {
    if (!reauthPwd) {
      toast.error("Mot de passe re-auth requis pour sauvegarder.");
      return;
    }
    let mapping;
    try {
      mapping = JSON.parse(mappingJson);
    } catch (e) {
      toast.error("fieldMapping JSON invalide.");
      return;
    }
    try {
      await api.put("/appointments/connector", {
        providerType: form.providerType,
        host: form.host || null,
        port: form.port ? Number(form.port) : null,
        database: form.database || null,
        username: form.username || null,
        password: form.password || null,
        tableName: form.tableName || null,
        syncFrequencyMinutes: Number(form.syncFrequencyMinutes) || 15,
        fieldMapping: mapping,
        allowPrivateNetwork: !!form.allowPrivateNetwork,
        privateNetworkJustification: form.privateNetworkJustification || null,
        reauthPassword: reauthPwd,
      });
      toast.success("Configuration sauvegardée");
      setReauthPwd("");
      setForm((f) => ({ ...f, password: "" }));
      await fetchAll();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Échec sauvegarde");
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/appointments/connector/test");
      setTestResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec test");
    } finally {
      setTesting(false);
    }
  };

  const runDryRun = async () => {
    setDryRunning(true);
    setDryResult(null);
    try {
      const sample = JSON.parse(sampleJson);
      const { data } = await api.post("/appointments/connector/dry-run", { sampleRecord: sample });
      setDryResult(data);
    } catch (e) {
      if (e instanceof SyntaxError) toast.error("Échantillon JSON invalide");
      else toast.error(e?.response?.data?.detail || "Échec dry-run");
    } finally {
      setDryRunning(false);
    }
  };

  const changeMode = async (mode) => {
    const pwd = window.prompt(`Confirmer en saisissant votre mot de passe pour passer en mode ${mode}`);
    if (!pwd) return;
    try {
      await api.patch("/appointments/connector/mode", { enabledMode: mode, reauthPassword: pwd });
      toast.success(`Mode → ${mode}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec changement de mode");
    }
  };

  const triggerSync = async () => {
    const pwd = window.prompt("Confirmer en saisissant votre mot de passe pour lancer la sync");
    if (!pwd) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/appointments/connector/sync-now", { reauthPassword: pwd });
      toast.info(`Run : ${data.status}`);
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec sync");
    } finally {
      setSyncing(false);
    }
  };

  const currentMode = config?.enabledMode || "DISABLED";

  return (
    <div className="space-y-6 mt-4" data-testid="rdv-connector-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Plug className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-800">Connecteur RDV (couche 1)</h2>
            <p className="text-xs text-slate-500">
              Infrastructure prête à brancher un logiciel de clinique — aucune sync réelle tant que le provider concret n'est pas livré.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${MODE_STYLES[currentMode]}`} data-testid="current-mode">
            {currentMode}
          </span>
          <button onClick={fetchAll} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {form.allowPrivateNetwork && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-start gap-3">
          <Lock className="w-5 h-5 text-red-600 mt-0.5" />
          <div className="text-sm text-red-800">
            <strong>Réseau privé autorisé</strong> pour ce connecteur — les tests/syncs vers des IP internes sont permis.
            Justification : <em>{form.privateNetworkJustification || "(vide)"}</em>
          </div>
        </div>
      )}

      {/* Configuration */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <h3 className="text-sm font-bold text-slate-700">Configuration</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Provider technique</span>
            <select
              value={form.providerType}
              onChange={(e) => setForm({ ...form, providerType: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2"
              data-testid="input-providerType"
            >
              {providers.map((p) => (
                <option key={p.provider_type} value={p.provider_type}>
                  {p.provider_type}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Host</span>
            <input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-host" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Port</span>
            <input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-port" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Database / resource path</span>
            <input value={form.database} onChange={(e) => setForm({ ...form, database: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-database" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Username</span>
            <input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-username" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Password{config?.password === "••••••••" ? " (laisser vide = inchangé)" : ""}</span>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={config?.password === "••••••••" ? "••••••••" : ""}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-password" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Table / endpoint</span>
            <input value={form.tableName} onChange={(e) => setForm({ ...form, tableName: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-tableName" />
          </label>
          <label className="text-sm">
            <span className="block text-slate-600 mb-1">Sync toutes les N minutes</span>
            <input type="number" min="1" max="1440" value={form.syncFrequencyMinutes}
              onChange={(e) => setForm({ ...form, syncFrequencyMinutes: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-freq" />
          </label>
          <label className="text-sm flex items-center gap-2 mt-6">
            <input type="checkbox" checked={form.allowPrivateNetwork}
              onChange={(e) => setForm({ ...form, allowPrivateNetwork: e.target.checked })}
              data-testid="input-allow-private" />
            <span className="text-slate-700">Autoriser réseau privé (LAN/VPN)</span>
          </label>
        </div>

        {form.allowPrivateNetwork && (
          <label className="block text-sm">
            <span className="block text-slate-600 mb-1">Justification (≥ 20 caractères, obligatoire)</span>
            <textarea rows={2}
              value={form.privateNetworkJustification}
              onChange={(e) => setForm({ ...form, privateNetworkJustification: e.target.value })}
              className="w-full border border-slate-200 rounded-lg px-3 py-2"
              data-testid="input-private-justification" />
          </label>
        )}

        <label className="block text-sm">
          <span className="block text-slate-600 mb-1">fieldMapping (JSON strict)</span>
          <textarea rows={10} value={mappingJson} onChange={(e) => setMappingJson(e.target.value)}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg px-3 py-2"
            data-testid="input-mapping-json" />
        </label>

        <div className="flex items-end gap-3">
          <label className="text-sm flex-1">
            <span className="block text-slate-600 mb-1">Mot de passe pour sauvegarder (re-auth)</span>
            <input type="password" value={reauthPwd} onChange={(e) => setReauthPwd(e.target.value)}
              className="w-full border border-slate-200 rounded-lg px-3 py-2" data-testid="input-reauth-save" />
          </label>
          <button onClick={saveConfig} disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 disabled:opacity-50"
            data-testid="save-config-btn">
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Actions + Mode */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-bold text-slate-700">Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={runTest} disabled={testing || !config}
              className="flex items-center gap-2 px-3 py-2 bg-slate-800 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="test-btn">
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              Tester la connexion
            </button>
            <button onClick={runDryRun} disabled={dryRunning}
              className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="dry-run-btn">
              {dryRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
              Dry-run mapping
            </button>
            <button onClick={triggerSync} disabled={syncing || currentMode === "DISABLED"}
              className="flex items-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg text-sm disabled:opacity-50"
              data-testid="sync-now-btn">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync maintenant
            </button>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500 mb-2">Mode d'exécution</p>
            <div className="flex gap-2">
              {["DISABLED", "DRY_RUN", "ACTIVE"].map((m) => (
                <button key={m} onClick={() => changeMode(m)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                    currentMode === m ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                                      : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`mode-${m}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dry-run sample */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-sm font-bold text-slate-700 mb-2">Échantillon pour dry-run</h3>
          <textarea rows={6} value={sampleJson} onChange={(e) => setSampleJson(e.target.value)}
            className="w-full font-mono text-xs border border-slate-200 rounded-lg px-3 py-2"
            data-testid="input-sample-json" />
          {dryResult && (
            <div className="mt-3 text-xs" data-testid="dry-result">
              <p className="font-semibold text-slate-700 mb-1">Résultat :</p>
              <pre className="bg-slate-50 p-2 rounded overflow-x-auto">{JSON.stringify(dryResult, null, 2)}</pre>
            </div>
          )}
        </div>
      </div>

      {/* Test result */}
      {testResult && (
        <div className={`border rounded-xl p-5 ${READINESS_STYLES[testResult.overall_readiness] || ""}`}
             data-testid="test-result">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-bold">Résultat du test — {testResult.provider_type}</h3>
              <p className="text-xs mt-0.5 opacity-80">
                Verdict : <strong>{testResult.overall_readiness}</strong>
              </p>
            </div>
          </div>
          <div className="space-y-1.5">
            {testResult.stages.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-sm" data-testid={`stage-${s.stage}`}>
                {STAGE_ICON[s.status]}
                <div className="flex-1">
                  <span className="font-mono text-xs text-slate-500 uppercase">{s.stage}</span>
                  <span className="ml-2 text-xs font-semibold">{s.status}</span>
                  {s.latency_ms != null && (<span className="ml-2 text-xs text-slate-500">{s.latency_ms} ms</span>)}
                  <p className="text-slate-700 text-xs">{s.message}</p>
                </div>
              </div>
            ))}
          </div>
          {testResult.blockers?.length > 0 && (
            <div className="mt-3 text-xs">
              <strong>Bloquants :</strong>
              <ul className="list-disc ml-5 mt-1">
                {testResult.blockers.map((b, i) => (<li key={i}>{b}</li>))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Last sync runs */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="text-sm font-bold text-slate-700 mb-3">Derniers runs de sync</h3>
        {runs.length === 0 ? (
          <p className="text-xs text-slate-400">Aucun run pour l'instant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="runs-table">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b border-slate-100">
                  <th className="py-1.5">Démarré</th>
                  <th>Mode</th>
                  <th>Statut</th>
                  <th>Fetched</th>
                  <th>Importés</th>
                  <th>MAJ</th>
                  <th>Erreurs</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5 text-xs text-slate-600">{r.startedAt}</td>
                    <td><span className={`text-xs px-2 py-0.5 rounded-full ${MODE_STYLES[r.mode]}`}>{r.mode}</span></td>
                    <td className="text-xs">{r.status}</td>
                    <td className="text-xs">{r.stats?.fetched ?? 0}</td>
                    <td className="text-xs">{r.stats?.imported ?? 0}</td>
                    <td className="text-xs">{r.stats?.updated ?? 0}</td>
                    <td className="text-xs">{r.stats?.errors ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
